import BaseService from './BaseService';
import apiClient from '../plugins/axios';

/**
 * Authentication Service
 */
class AuthService extends BaseService {
  constructor() {
    super('auth');
  }

  /**
   * Kullanıcı giriş işlemi
   * @param {Object} credentials - Giriş bilgileri (email, password)
   * @returns {Promise}
   */
  async login(credentials) {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      // Swagger response wrapper yapısı: LoginResponse direkt access_token ve refresh_token döndürüyor
      // Eğer wrapper varsa data'ya, yoksa direkt response.data'ya bak
      const loginData = response.data.data || response.data;
      const { access_token, refresh_token } = loginData;

      // Store tokens in localStorage
      if (access_token) {
        localStorage.setItem('authToken', access_token);
      }
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }

      return { access_token, refresh_token };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async signUp(credentials) {
    try {
      await apiClient.post('/auth/register', credentials);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Forgot password: email'e sıfırlama linki gönderilmesini iste
   * @param {string} email
   * @returns {Promise<{ message?: string }>}
   */
  async forgotPassword(email) {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      return response.data.data || response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reset token'ı doğrula (link tıklanınca). Token expire ise 400 döner.
   * Swagger: POST /auth/validate-reset-token, body: { token }
   * @param {string} token - Query'den gelen token
   * @returns {Promise<{ valid: boolean }>}
   */
  async verifyResetToken(token) {
    try {
      const response = await apiClient.post('/auth/validate-reset-token', { token });
      return response.data.data ?? response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Yeni şifre ile sıfırla (token valid doğrulandıktan sonra).
   * Swagger: POST /auth/reset-password, body: { token, password }
   * @param {string} token
   * @param {string} newPassword
   * @returns {Promise}
   */
  async resetPassword(token, newPassword) {
    try {
      await apiClient.post('/auth/reset-password', {
        token,
        password: newPassword,
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Kullanıcı çıkış işlemi
   * @returns {Promise}
   */
  async logout() {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Clear local storage even if logout API fails
      console.warn('Logout API call error:', error);
    } finally {
      // Remove tokens from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    }
  }

  /**
   * Giriş yapmış kullanıcının profil, rol ve izinlerini getir. GET /auth/me
   * @returns {Promise<{ id, email, given_name, family_name, roles: string[], permissions: string[] }>}
   */
  async me() {
    const response = await apiClient.get('/auth/me');
    return response.data.data || response.data;
  }

  /**
   * Access token'ı refresh token ile yenile. POST /auth/refresh
   * @returns {Promise<string>} Yeni access_token
   */
  async refreshToken() {
    const refresh_token = localStorage.getItem('refreshToken');
    if (!refresh_token) {
      throw new Error('No refresh token');
    }
    const response = await apiClient.post('/auth/refresh', { refresh_token });
    const data = response.data.data || response.data;
    const { access_token, refresh_token: newRefreshToken } = data || {};
    if (access_token) {
      localStorage.setItem('authToken', access_token);
    }
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }
    return access_token;
  }

  /**
   * Token geçerlilik kontrolü
   * @returns {boolean}
   */
  isTokenValid() {
    const token = localStorage.getItem('authToken');
    if (!token) return false;

    try {
      // JWT token'ı decode et ve süresini kontrol et
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Token decode error:', error);
      return false;
    }
  }

  /**
   * Access token'ı localStorage'dan getir
   * @returns {string|null}
   */
  getToken() {
    return localStorage.getItem('authToken');
  }

  /**
   * Refresh token'ı localStorage'dan getir
   * @returns {string|null}
   */
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  /**
   * JWT token'dan kullanıcı bilgilerini decode et
   * @returns {Object|null}
   */
  getUserFromToken() {
    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch (error) {
      console.error('Token decode error:', error);
      return null;
    }
  }
}

export default new AuthService();
